import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { automationsApi } from '../api/automationsApi';

export type DbNotificationRule = {
  id: string;
  name: string;
  description?: string;
  event_trigger: string;
  conditions?: any;
  template_id?: string;
  is_active: boolean;
  action?: string; // UI only property
};

export function useRulesEngine() {
  const [rules, setRules] = useState<DbNotificationRule[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRules = async () => {
    try {
      setLoading(true);
      try {
        const data = await automationsApi.getNotificationRules();
        if (data && data.length > 0) {
          setRules(data as DbNotificationRule[]);
          return;
        }
      } catch (e) {
        // Fallback
      }

      if (supabase) {
        const { data, error } = await supabase.from('notification_rules').select('*');
        if (!error && data) {
          setRules(data as DbNotificationRule[]);
        }
      }
    } catch (e) {
      console.error('Failed to fetch rules', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const saveRule = async (rule: Partial<DbNotificationRule>) => {
    const payload = {
      name: rule.name || '',
      event_trigger: rule.event_trigger || '',
      conditions: rule.conditions || {},
      template_id: rule.template_id || null,
      is_active: rule.is_active ?? true
    };

    try {
      if (rule.id && !rule.id.startsWith('rule-')) {
        const data = await automationsApi.updateNotificationRule(rule.id, payload);
        if (data) {
          setRules(prev => prev.map(r => r.id === rule.id ? (data as DbNotificationRule) : r));
          return;
        }
      } else {
        const data = await automationsApi.createNotificationRule(payload);
        if (data) {
          setRules(prev => [...prev, data as DbNotificationRule]);
          return;
        }
      }
    } catch (e) {
      // Fallback to supabase
      if (supabase) {
        if (rule.id && !rule.id.startsWith('rule-')) {
          const { data, error } = await supabase.from('notification_rules').update(payload).eq('id', rule.id).select().single();
          if (!error && data) setRules(prev => prev.map(r => r.id === rule.id ? (data as DbNotificationRule) : r));
        } else {
          const { data, error } = await supabase.from('notification_rules').insert([payload]).select().single();
          if (!error && data) setRules(prev => [...prev, data as DbNotificationRule]);
        }
      }
    }
  };

  const deleteRule = async (id: string) => {
    try {
      if (!id.startsWith('rule-')) {
        await automationsApi.deleteNotificationRule(id);
      }
    } catch (e) {
      if (supabase && !id.startsWith('rule-')) {
        await supabase.from('notification_rules').delete().eq('id', id);
      }
    }
    setRules(prev => prev.filter(r => r.id !== id));
  };

  return { rules, loading, saveRule, deleteRule };
}
